import { useState, useEffect } from 'react';
import { UserRoles } from '@bb/users';
import { TeamController } from 'api';
import errorReporter from '@app/errorReporter';
import { useDebounce } from 'hooks';
import { message } from 'components';
import useIsBranchAdmin from './useIsBranchAdmin';
import type { TeamMember } from '../util';
import { compareFirstAndLastName } from '../util';

export interface TeamInfo {
  id: string;
  name: string;
  teamMlsId?: string;
  email?: string;
  website?: string;
  phone?: string;
  createdAt: string;
  orgId: string;
  updatedAt: string;
  users: TeamMember[];
}

const useGetTeamMembers = ({
  teamId,
  searchText,
}: {
  teamId: string;
  searchText: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [teamInfo, setTeamInfo] = useState<TeamInfo | undefined>(undefined);
  const debouncedSearchText = useDebounce(searchText, 500);
  const [unfilteredUsers, setUnfilteredUsers] = useState<TeamMember[]>([]);
  const { isBranchAdmin, branchAdminCanEdit } = useIsBranchAdmin();

  const fetchTeamMembers = async (active: boolean) => {
    try {
      setIsLoading(true);
      const memberList = await TeamController.get(teamId);
      if (!active) return;

      // Filter out users for branch admins to keep an accurate count
      const users = memberList.users.filter(
        user => !isBranchAdmin || branchAdminCanEdit(user),
      );
      users.sort(compareFirstAndLastName);

      setTeamInfo({ ...memberList, users });
      setUnfilteredUsers(users);
    } catch (err) {
      errorReporter.error(err);
      setTeamInfo(undefined);
      message.error('Error loading team members');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!teamId) return;
    let active = true;

    fetchTeamMembers(active);

    return () => {
      active = false;
    };
  }, [teamId]);

  useEffect(() => {
    setTeamInfo(info =>
      info
        ? {
            ...info,
            users: unfilteredUsers.filter(({ firstName, lastName }) =>
              `${firstName} ${lastName}`
                .toLowerCase()
                .includes(debouncedSearchText.toLowerCase()),
            ),
          }
        : undefined,
    );
  }, [debouncedSearchText, unfilteredUsers]);

  return {
    fetchTeamMembers,
    isLoading,
    teamInfo,
    teamMembersCount: teamInfo?.users?.length || 0,
  };
};

export default useGetTeamMembers;
